import React, { useEffect } from 'react';
import moment, { Moment } from 'moment-timezone';
import { getRealTimezoneMoment } from 'utils/timeUtils';

import { BOOK_SAVING } from 'constants/bookingConstants';

import { LoadType, DriverInfo } from 'sharedTypes/loads/loadType';
import { LocationSearchType } from 'sharedTypes/location/locationTypes';
import { OfferType } from 'sharedTypes/offers/offers';
import { AppointmentType, DEFAULT_COUNTRY_SUGGESTION_ID } from 'constants/loadConstants/loadConstants';

/* Types */
type setBinStatusType = (nextBinStatus: string) => void;
type genericBooleanSetter = (nextState: boolean) => void;

/* Consts */
const isValidTimeRegex = /^(\d|0\d|1\d|2[0-3])((\d)?(:)?(:(\d|[0-5]\d))?)$/;

//Moment Formats
const LONG_FORMAT = 'MM/DD/YYYY HH:mm';
const SHORT_FORMAT = 'MM/DD/YYYY';
/* Helpers */
export const isNotSameDay = (load?: LoadType, offer?: OfferType) => {
  const rawPickupEarly = load?.PickupApptEarliest || load?.PickupDateEarliest || offer?.originEarliest;
  const rawTimezone = load?.PickupLocationIANACode || offer?.originCityIANATimeZoneName;

  const pickupEarliestMoment = getRealTimezoneMoment(rawPickupEarly, rawTimezone);
  const nowMoment = moment();

  const hoursTillPickup = pickupEarliestMoment.diff(nowMoment, 'hours')
  return hoursTillPickup > 24;
}

export const getEmptyDateTime = (dateStr: string, timezone: string) => {
  const emptyDateTimeWithTZ = moment.tz(dateStr, LONG_FORMAT, timezone);
  return emptyDateTimeWithTZ.utc().format(LONG_FORMAT);
}

const replaceCommasIfNeeded = (string: string) => {
  if (string && (string.lastIndexOf(",") !== -1)) {
    const index = string.lastIndexOf(",")

    const p1 = string.slice(0, index)
    const p2 = string.slice(index + 1)
    return p1 + " and" + p2;
  }
  return string;
}

export const getBINLoadRequirements = (load: LoadType) => {
  const requirements = [];

  if (load.EquipmentType === 'R' || load.LoadType === 'Reefer') {
    requirements.push('Reefer')
  }
  if (load.IsOverSizeLoad) {
    requirements.push('oversized load')
  }

  if (load.TwicNeeded) {
    requirements.push('TWIC')
  }

  if (load.IsHazmat) {
    requirements.push('hazmat')
  }

  if (load.IsTeam) {
    requirements.push('team')
  }

  if (load.DropTrailer || load.PickupApptTypeId === AppointmentType.DropTrailer || load.DeliveryApptTypeId === AppointmentType.DropTrailer) {
    requirements.push('drop trailer')
  }

  if (load.TankerEndorsement) {
    requirements.push('tanker endorsed')
  }

  if (load.NumberOfPickups > 1 || load.NumberOfDeliveries > 1) {
    requirements.push('multistop')
  }

  const requirmentsString = requirements.join(', ')
  return replaceCommasIfNeeded(requirmentsString);

}

export const getSpecialEquipment = (loadDetails: LoadType) => {
  const specialEquipment = [];

  if (loadDetails.FoodGradeRequired) {
    specialEquipment.push('food grade');
  }
  if (loadDetails.HighCubeRequired) {
    specialEquipment.push('high cube');
  }
  if (loadDetails.LiftGateRequired) {
    specialEquipment.push('lift gate');
  }
  if (loadDetails.LoadBarsRequired) {
    specialEquipment.push('load bars');
  }
  if (loadDetails.PlateTrailerRequired) {
    specialEquipment.push('plate trailer')
  }
  if (loadDetails.RollDoorsRequired) {
    specialEquipment.push('roll doors');
  }
  if (loadDetails.StrapsNeededRequired) {
    specialEquipment.push('straps');
  }
  if (loadDetails.TarpsRequired) {
    specialEquipment.push('tarps');
  }

  const specialEquipmentString = specialEquipment.join(', ');
  return replaceCommasIfNeeded(specialEquipmentString);
}

export const canBookItNow = (dateTimeRequired: boolean, emptyCity: LocationSearchType, emptyDate: moment.Moment, emptyTime: string) => {
  const emptyCityValid = (emptyCity !== null && emptyCity?.CityState && emptyCity?.CityName)

  if (!dateTimeRequired) {
    return emptyCityValid;
  }
  const emptyDateValid = (emptyDate && moment(emptyDate, ['MM/DD/YYYY', 'M/DD/YYYY', 'MM/D/YYYY', 'M/D/YYYY'], true).isValid())
  const emptyTimeValid = (emptyTime && emptyTime.length === 5 && isValidTimeRegex.test(emptyTime))

  return emptyCityValid && emptyDateValid && emptyTimeValid;
}

export const getEmptyTimeString = (isSameDay: boolean, loadDetails: LoadType, emptyDate: Moment, emptyTime: string, emptyCity: LocationSearchType) => {
  let emptyTimeString = "";

  if (!isSameDay) {
    let emptyTimeMoment = null;
    const yesterdayMoment = moment().set({ hour: 10, minute: 0, second: 0 }).subtract(1, 'd');
    emptyTimeMoment = yesterdayMoment;

    if (moment(loadDetails.PickupApptEarliest).isBefore(yesterdayMoment)) {
      emptyTimeMoment = moment(loadDetails.PickupApptEarliest).set({ hour: 10, minute: 0, second: 0 }).subtract(1, 'd')
    }

    emptyTimeString = emptyTimeMoment.format(LONG_FORMAT)
  } else {
    emptyTimeString = getEmptyDateTime(`${emptyDate?.format(SHORT_FORMAT)} ${emptyTime || '00:00'}`, emptyCity?.LocationIANACode || '')
  }

  return emptyTimeString;
}

export const getHasTruckAndDriverInfo = (truckAndDriverInfo: DriverInfo) => {
  return Object.values(truckAndDriverInfo).filter(value => typeof value === 'string').some((value: string) => !!value)
}

export const fillTruckAndDriverInfo = (load?: LoadType): DriverInfo => {
  return {
    Driver: load?.DriverInfo?.Driver || '',
    SecondDriver: load?.DriverInfo?.SecondDriver || '',
    DriverPhone: load?.DriverInfo?.DriverPhone || '',
    DriverPhoneCountryId: load?.DriverInfo?.DriverPhoneCountryId || DEFAULT_COUNTRY_SUGGESTION_ID,
    SecondDriverPhoneNumber: load?.DriverInfo?.SecondDriverPhoneNumber || '',
    TrailerNumber: load?.DriverInfo?.TrailerNumber || '',
    TruckNumber: load?.DriverInfo?.TruckNumber || '',
    SecondDriverPhoneCountryId: load?.DriverInfo?.SecondDriverPhoneCountryId || DEFAULT_COUNTRY_SUGGESTION_ID
  }
}

/* Reusable Hooks */
export const useSetBookingModalUnmounted = (isMounted: React.MutableRefObject<boolean>) => {
  useEffect(() => {
    return () => {
      isMounted.current = false
    };
  }, [isMounted])
};

export const useSetBookingModalLoading = (isLoading: boolean, setBinStatus: setBinStatusType) => {
  useEffect(() => {
    if (isLoading) {
      setBinStatus(BOOK_SAVING);
    }
  }, [isLoading, setBinStatus])
}

export const useHasTruckDriverInfo = (newDriverInfo: DriverInfo, setHasTruckAndDriverInfo: genericBooleanSetter) => {
  useEffect(() => {
    setHasTruckAndDriverInfo(getHasTruckAndDriverInfo(newDriverInfo))
  }, [newDriverInfo, setHasTruckAndDriverInfo]);
};

export const prettifyIfLocationName = (input: string) => {
  const split = input.split(',');
  return `${split[0].toLowerCase()},${split[1]}`;
};
